import React, { useState } from 'react';
import { db } from '../../firebase';
import { collection, addDoc } from 'firebase/firestore';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/system';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';

// Modern color palette (consistent with AddTopics.jsx)
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  background: '#f5f7fa',
  accent: '#3498db',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

// Styled components
const ModernCard = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  background: 'white',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(3),
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1.25, 2.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: colors.accent,
    color: 'white',
  },
}));

const AddPastPapers = () => {
  const [pastPaper, setPastPaper] = useState({
    title: '',
    description: '',
    year: '',
    subject: '',
    category: '', // Added category state
    schoolLevel: '',
    url: '',
    solutionsUrl: '',
    accessStatus: 'premium',
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Handle form input changes
  const handleChange = (e) => {
    setPastPaper({ ...pastPaper, [e.target.name]: e.target.value });
  };

  // Handle Snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // Validate URL format
  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!pastPaper.title.trim()) {
      setSnackbarMessage('Title is required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!pastPaper.description.trim()) {
      setSnackbarMessage('Description is required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!pastPaper.year || isNaN(pastPaper.year) || pastPaper.year < 1900 || pastPaper.year > new Date().getFullYear()) {
      setSnackbarMessage('Please enter a valid year');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!pastPaper.subject.trim()) {
      setSnackbarMessage('Subject is required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!pastPaper.category.trim()) {
      setSnackbarMessage('Category is required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!pastPaper.schoolLevel.trim()) {
      setSnackbarMessage('School Level is required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!pastPaper.url.trim()) {
      setSnackbarMessage('Download URL is required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!isValidUrl(pastPaper.url)) {
      setSnackbarMessage('Please enter a valid Download URL');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (pastPaper.solutionsUrl && !isValidUrl(pastPaper.solutionsUrl)) {
      setSnackbarMessage('Please enter a valid Solutions URL');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!pastPaper.accessStatus) {
      setSnackbarMessage('Access Status is required');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      await addDoc(collection(db, 'pastPapers'), {
        title: pastPaper.title.trim(),
        description: pastPaper.description.trim(),
        year: parseInt(pastPaper.year, 10),
        subject: pastPaper.subject.trim(),
        category: pastPaper.category.trim(), // Added category field to Firestore
        schoolLevel: pastPaper.schoolLevel.trim(),
        url: pastPaper.url.trim(),
        solutionsUrl: pastPaper.solutionsUrl ? pastPaper.solutionsUrl.trim() : '',
        accessStatus: pastPaper.accessStatus,
        createdAt: new Date(),
      });
      setPastPaper({ title: '', description: '', year: '', subject: '', category: '', schoolLevel: '', url: '', solutionsUrl: '', accessStatus: 'premium' });
      setSnackbarMessage('Past paper added successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error adding past paper:', { message: error.message, code: error.code });
      setSnackbarMessage('Error adding past paper');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const accessStatusOptions = [
    { value: 'trial', label: 'Trial' },
    { value: 'premium', label: 'Premium' },
  ];

  return (
    <div style={{ background: colors.background }}>
      <Container sx={{ py: 8, minHeight: '100vh', mt: 5 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 5,
            p: 4,
            borderRadius: '16px',
            background: `linear-gradient(159deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
            color: 'white',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: '700', display: 'flex', alignItems: 'center' }}>
            <LibraryBooksIcon sx={{ fontSize: 35, mr: 1 }} />
            Add New Past Paper
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
            Add a new past paper to the Maths Gem Seychelles portal.
          </Typography>
        </Box>

        {/* Form */}
        <ModernCard sx={{ maxWidth: 600, mx: 'auto' }}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Past Paper Title"
              name="title"
              value={pastPaper.title}
              onChange={handleChange}
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                '& .MuiInputLabel-root': {
                  color: colors.secondary,
                  '&.Mui-focused': { color: colors.accent },
                },
              }}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={pastPaper.description}
              onChange={handleChange}
              multiline
              rows={4}
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                '& .MuiInputLabel-root': {
                  color: colors.secondary,
                  '&.Mui-focused': { color: colors.accent },
                },
              }}
            />
            <TextField
              fullWidth
              label="Year"
              name="year"
              value={pastPaper.year}
              onChange={handleChange}
              type="number"
              required
              inputProps={{ min: 1900, max: new Date().getFullYear() }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                '& .MuiInputLabel-root': {
                  color: colors.secondary,
                  '&.Mui-focused': { color: colors.accent },
                },
              }}
            />
            <TextField
              fullWidth
              label="Subject"
              name="subject"
              value={pastPaper.subject}
              onChange={handleChange}
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                '& .MuiInputLabel-root': {
                  color: colors.secondary,
                  '&.Mui-focused': { color: colors.accent },
                },
              }}
            />
            <TextField
              fullWidth
              label="Past Paper Category"
              name="category"
              value={pastPaper.category}
              onChange={handleChange}
              required
              placeholder="e.g. Mock Exams, Final Exams, End of Term"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                '& .MuiInputLabel-root': {
                  color: colors.secondary,
                  '&.Mui-focused': { color: colors.accent },
                },
              }}
            />
            <TextField
              fullWidth
              label="School Level"
              name="schoolLevel"
              value={pastPaper.schoolLevel}
              onChange={handleChange}
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                '& .MuiInputLabel-root': {
                  color: colors.secondary,
                  '&.Mui-focused': { color: colors.accent },
                },
              }}
            />
            <TextField
              fullWidth
              label="Download URL"
              name="url"
              value={pastPaper.url}
              onChange={handleChange}
              required
              type="url"
              placeholder="https://example.com/pastpaper.pdf"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                '& .MuiInputLabel-root': {
                  color: colors.secondary,
                  '&.Mui-focused': { color: colors.accent },
                },
              }}
            />
            <TextField
              fullWidth
              label="Solutions URL"
              name="solutionsUrl"
              value={pastPaper.solutionsUrl}
              onChange={handleChange}
              type="url"
              placeholder="https://example.com/solutions.pdf"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                '& .MuiInputLabel-root': {
                  color: colors.secondary,
                  '&.Mui-focused': { color: colors.accent },
                },
              }}
            />
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: colors.secondary }}>Access Status</InputLabel>
              <Select
                name="accessStatus"
                value={pastPaper.accessStatus}
                onChange={handleChange}
                label="Access Status"
                required
                sx={{
                  borderRadius: '10px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                  '& .MuiInputLabel-root': {
                    color: colors.secondary,
                    '&.Mui-focused': { color: colors.accent },
                  },
                }}
              >
                {accessStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <AnimatedButton
              type="submit"
              variant="contained"
              color="primary"
            >
              Add Past Paper
            </AnimatedButton>
          </form>
        </ModernCard>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: '100%', bgcolor: snackbarSeverity === 'success' ? colors.primary : '#e74c3c', color: 'white' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};

export default AddPastPapers;